import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {connect, useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {hdp, wdp, getByScreenSize, isWeb} from '../../utils/responsive';
import TextGeneric from '../../components/textGeneric';
import {Theme} from '../../theme/lightTheme';
import {SpecialityModalProps} from '../../interfaces/connected-components/specialityModal';
import {closeSpecialityModal} from './actions';
import {tr} from '../../translations';
import {updateUser} from '../../screens/content/profile/actions';
import Config from 'react-native-config';
import Animated, {FadeInDown} from 'react-native-reanimated';
import GenericButton from '../../components/button';
import {
  BackHandler,
  Dimensions,
  Platform,
  RefreshControl,
  View,
  TouchableOpacity,
} from 'react-native';
import SearchListSkeleton from '../../components/searchListSkeleton';
import {updateConfig} from '../../screens/auth/login/actions';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {openQuestionModal} from '../questionModal/actions';
import {modalTypes} from '../../utils/enums';
import Icon from '../../components/icon';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';
import BottomSheet, {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetFlatList,
} from '@gorhom/bottom-sheet';
import {Space} from '../../screens/content/home';
import BottomSheetHandle from '../../components/bottomSheetHandle';
import '../../utils/webAnimations';

// Web-specific modal styles
const WebOverlayContainer = styled.TouchableOpacity`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1998;
`;

const WebModalContainer = styled.View`
  width: ${getByScreenSize(wdp(95), wdp(40))}px;
  max-height: 90vh;
  background-color: ${({theme}) => theme.screenBackground};
  border-radius: 8px;
  overflow: hidden;
  shadow-color: #000;
  shadow-offset: 0px 5px;
  shadow-opacity: 0.2;
  shadow-radius: 15px;
  elevation: 10;
  ${isWeb ? 'box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.2);' : ''}
`;

const WebHeaderRow = styled.View`
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  border-bottom-width: 2px;
  border-bottom-color: ${({theme}) => theme.border + '33'};
`;

const WebCloseButton = styled.TouchableOpacity`
  padding: 8px;
  position: absolute;
  right: 10px;
  top: 10px;
`;

const WebContent = styled.View`
  padding: 20px;
`;

const WebScrollView = styled.ScrollView`
  max-height: 70vh;
`;

const Title = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s6}px;
  color: ${({theme}) => theme.text.dark};
  letter-spacing: 1px;
  text-align: center;
  width: 100%;
  padding-top: 1%;
  padding-bottom: 3%;
  padding-horizontal: 5%;
`;
const SpecialityItem = styled(GenericButton)<{selected?: boolean}>`
  border-radius: 100px;
  border-color: ${({theme}) => theme.text.dark + '22'};
  border-width: ${({selected}) => (selected ? 0 : 1)}px;
  background-color: ${({theme, selected}) =>
    selected ? theme.primary : 'transparent'};
  margin-bottom: 3%;
`;

const SpecialityModal = ({specialityModal}: SpecialityModalProps) => {
  /**
   * Represents the current theme.
   * @type {Theme}
   */
  const theme = useTheme() as Theme;
  const {isVisible} = specialityModal || {};
  const {top: topInset, bottom: bottomInset} = useSafeAreaInsets();
  const {isMobile} = useResponsiveDimensions();
  const sheetRef = useRef<BottomSheet>(null);
  const snapPoints = useMemo(
    () => [Dimensions.get('screen').height - Math.max(topInset || 0, hdp(6))],
    [],
  );
  const {currentUser, config: userConfig} = useSelector(
    (state: RootState) => state.auth,
  );

  useEffect(() => {
    if (isVisible) {
      if (!(isWeb && !isMobile)) {
        sheetRef.current?.snapToIndex(0);
      }
      if ((userConfig?.specialties || [])?.length === 0) {
        updateConfig();
      }
    } else {
      if (!(isWeb && !isMobile)) {
        sheetRef.current?.close();
      }
    }
  }, [isVisible, isMobile]);

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        opacity={0.1}
        {...props}
        onPress={() => sheetRef.current?.close()}
        pressBehavior={'collapse'}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
      />
    ),
    [],
  );

  const _renderFooter = () => {
    return <Space bottomInset={bottomInset} />;
  };

  // Handle back press from detail screen
  const handleBackPress = () => {
    if (isVisible) {
      closeSpecialityModal();
      return true;
    }
    return false;
  };

  // Set up the back button listener
  useEffect(() => {
    if (Platform.OS !== 'web') {
      const backHandlerSubscription = BackHandler.addEventListener(
        'hardwareBackPress',
        handleBackPress,
      );

      return () => {
        backHandlerSubscription.remove();
      };
    }
  }, [handleBackPress]);

  const onItemClicked = async (item: any) => {
    const {
      id,
      country_id,
      date_of_birth,
      first_name,
      gender,
      last_name,
      language_id,
      profession_id,
    } = currentUser || {};
    const body: any = {
      id,
      specialty_id: item?.id,
      country_id,
      date_of_birth,
      first_name,
      gender,
      last_name,
      language_id,
      profession_id,
    };
    await updateUser(body);
  };

  // Method to trigger when the bottom sheet snaps to a different position
  const handleSheetChange = useCallback((index: number) => {
    if (index === -1) {
      closeSpecialityModal();
    }
  }, []);

  const onClose = useCallback(() => {
    closeSpecialityModal();
  }, []);

  const handleCloseBottomSheet = useCallback(() => {
    if (sheetRef.current) {
      sheetRef.current.close();
    }
  }, []);

  const _renderItem = ({item}: {item: any}) => {
    const isSelected =
      (currentUser?.email || '').trim().toLowerCase() === Config.APPLE_EMAIL
        ? currentUser?.real_specialty_id === item?.id
        : currentUser?.specialty_id === item?.id;

    return (
      <Animated.View
        {...(Platform.OS === 'web'
          ? {className: 'screen-fade-in-down'}
          : {entering: FadeInDown.delay(200)})}>
        <SpecialityItem
          selected={isSelected}
          disabledNoOpacity={isSelected}
          label={item.name}
          labelStyle={{
            color: isSelected ? theme.button.text : theme.text.dark,
          }}
          onPress={() =>
            openQuestionModal({
              title: tr('chooseYourSpeciality.confirmTitle'),
              message:
                tr('chooseYourSpeciality.confirmMessage') + ` ${item?.name} ?`,
              onConfirm: () => onItemClicked(item),
              type: modalTypes.question,
            })
          }
          radius={100}
        />
      </Animated.View>
    );
  };

  const _renderEmpty = () => (
    <SearchListSkeleton extraSmall borderRadius={100} />
  );

  const renderSpecialties = () => {
    if ((userConfig?.specialties || []).length === 0) {
      return _renderEmpty();
    }
    return userConfig?.specialties?.map((item: any) => (
      <View key={item.id}>{_renderItem({item})}</View>
    ));
  };

  // Web-specific render
  if (isWeb && !isMobile) {
    if (!isVisible) return null;
    return (
      <WebOverlayContainer activeOpacity={1} onPress={closeSpecialityModal}>
        <WebModalContainer>
          <WebHeaderRow>
            <Title bold>{tr('chooseYourSpeciality.title')}</Title>
            <WebCloseButton onPress={closeSpecialityModal}>
              <Icon
                name={'x'}
                size={theme.text.s6}
                color={theme.secondary + 'AA'}
              />
            </WebCloseButton>
          </WebHeaderRow>
          <WebContent>
            <WebScrollView>{renderSpecialties()}</WebScrollView>
          </WebContent>
        </WebModalContainer>
        <TouchableOpacity
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: -1,
          }}
          onPress={closeSpecialityModal}
          activeOpacity={1}
        />
      </WebOverlayContainer>
    );
  }

  // Mobile render remains unchanged
  return (
    <>
      {isVisible && isWeb && (
        <View
          style={{
            width: wdp(100),
            height: hdp(100),
            position: 'absolute',
            top: 0,
            pointerEvents: 'box-none',
          }}
          onTouchStart={closeSpecialityModal}
          //@ts-ignore
          onClick={() => closeSpecialityModal()}
        />
      )}
      <BottomSheet
        enableDynamicSizing={false}
        style={{
          borderRadius: wdp(5),
          overflow: 'hidden',
          width: wdp(99),
          marginHorizontal: wdp(0.5),
          shadowColor: '#000',
          shadowOffset: {
            width: 0,
            height: 12,
          },
          shadowOpacity: 0.58,
          shadowRadius: 16.0,
          elevation: 24,
        }}
        animationConfigs={{
          duration: 250,
        }}
        ref={sheetRef}
        index={isVisible ? 0 : -1}
        backgroundStyle={{
          backgroundColor: theme.screenBackground,
        }}
        handleComponent={props => (
          <BottomSheetHandle
            {...props}
            closeBottomSheet={handleCloseBottomSheet}
          />
        )}
        bottomInset={bottomInset}
        enablePanDownToClose
        snapPoints={snapPoints}
        backdropComponent={renderBackdrop}
        onChange={handleSheetChange}
        onClose={onClose}>
        <Title bold>{tr('chooseYourSpeciality.title')}</Title>
        <BottomSheetFlatList
          data={userConfig?.specialties || []}
          renderItem={_renderItem}
          ListEmptyComponent={_renderEmpty}
          ListFooterComponent={_renderFooter}
          contentContainerStyle={{
            paddingHorizontal: '5%',
          }}
          refreshControl={
            <RefreshControl
              refreshing={false}
              onRefresh={updateConfig}
              tintColor={theme.primary}
            />
          }
        />
      </BottomSheet>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  specialityModal: state.specialityModal,
});

export default connect(mapStateToProps)(SpecialityModal);
